import React from "react";
import {
  Truck,
  Briefcase,
  ShieldCheck,
  Route,
  Fuel,
  Calculator,
  MapPin,
  Weight,
  Ruler,
  TrendingDown,
  Check,
  DollarSign,
  FileText,
  Building2,
  Wallet,
  ArrowUpRight,
  Signal,
  Wifi,
  BatteryFull,
} from "lucide-react";

// =============================================================================
// PHONE FRAME  —  same clean digital shell used across Mission Stories
// =============================================================================
const PhoneFrame = ({ children, testId }) => (
  <div
    className="mx-auto w-full max-w-[280px] rounded-[38px] p-1.5"
    data-testid={testId}
    style={{
      background: "linear-gradient(160deg, #10233C 0%, #04111F 100%)",
      border: "1px solid rgba(255,255,255,0.15)",
      boxShadow:
        "0 30px 60px -30px rgba(0,0,0,0.60), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.10)",
    }}
  >
    <div
      className="rounded-[32px] overflow-hidden relative"
      style={{
        background: "linear-gradient(180deg, #08182B 0%, #04111F 100%)",
      }}
    >
      <div className="relative h-6 flex items-center justify-center">
        <div
          aria-hidden="true"
          className="w-16 h-3.5 rounded-full"
          style={{
            background: "#04111F",
            border: "1px solid rgba(255,255,255,0.08)",
          }}
        />
      </div>
      <div className="flex items-center justify-between px-5 pb-1.5">
        <span
          className="text-[11px] font-semibold text-white"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          9:41
        </span>
        <div className="flex items-center gap-1 text-white/85">
          <Signal size={11} strokeWidth={2.5} />
          <Wifi size={11} strokeWidth={2.5} />
          <BatteryFull size={13} strokeWidth={2.5} />
        </div>
      </div>
      <div className="px-4 pt-1 pb-4">{children}</div>
      <div className="flex items-center justify-center pb-2 pt-1">
        <div
          aria-hidden="true"
          className="w-24 h-1 rounded-full"
          style={{ background: "rgba(255,255,255,0.35)" }}
        />
      </div>
    </div>
  </div>
);

const TopPill = ({ label, tone }) => {
  const palette = {
    amber: { accent: "#F4C752", bg: "rgba(244,199,82,0.14)", border: "rgba(244,199,82,0.45)" },
    purple: { accent: "#B7A5FF", bg: "rgba(183,165,255,0.14)", border: "rgba(183,165,255,0.45)" },
  }[tone];
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-[0.18em]"
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, color: palette.accent }}
    >
      <span
        className="w-1 h-1 rounded-full"
        style={{ background: palette.accent, boxShadow: `0 0 8px ${palette.accent}dd` }}
      />
      {label}
    </div>
  );
};

const ScreenTitle = ({ children }) => (
  <div
    className="mt-2 text-[15px] font-extrabold text-white leading-tight"
    style={{ fontFamily: "Sora, Inter, sans-serif" }}
  >
    {children}
  </div>
);

const MiniCard = ({ children, style, className = "" }) => (
  <div
    className={`rounded-xl p-2.5 ${className}`}
    style={{
      background: "rgba(255,255,255,0.05)",
      border: "1px solid rgba(255,255,255,0.10)",
      ...style,
    }}
  >
    {children}
  </div>
);

// =============================================================================
// TRUCKER MOCKS — HGV route, diesel intel, IFTA log
// =============================================================================
const MockHGVRoute = () => (
  <PhoneFrame testId="mock-hgv-route">
    <TopPill label="HGV Route" tone="amber" />
    <ScreenTitle>Denver → Jacksonville</ScreenTitle>
    <div
      className="mt-3 relative rounded-xl overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 30% 40%, rgba(244,199,82,0.18) 0%, rgba(244,199,82,0) 55%), linear-gradient(180deg, #071527 0%, #04111F 100%)",
        border: "1px solid rgba(255,255,255,0.10)",
        aspectRatio: "1 / 1",
      }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
          backgroundSize: "18px 18px",
        }}
      />
      <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <path
          d="M18,42 C50,60 80,80 110,110 C140,140 160,155 184,168"
          stroke="#F4C752"
          strokeWidth="2.5"
          strokeDasharray="5 4"
          fill="none"
          strokeLinecap="round"
        />
        {[{ x: 18, y: 42 }, { x: 110, y: 110 }, { x: 184, y: 168 }].map((p, i) => (
          <g key={i}>
            <circle cx={p.x} cy={p.y} r="6" fill="#F4C752" />
            <circle cx={p.x} cy={p.y} r="10" fill="#F4C752" opacity="0.25" />
          </g>
        ))}
      </svg>
      <div className="absolute top-2 left-2 text-[8px] uppercase tracking-[0.18em] text-white/60 font-bold">
        ORS · HGV
      </div>
      <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
        <div className="text-[9px] text-white font-semibold">Denver, CO</div>
        <div className="text-[9px] text-[#F4C752] font-bold">1,927 mi</div>
      </div>
    </div>
    <div className="mt-2.5 grid grid-cols-3 gap-1.5">
      {[
        { icon: Ruler, k: "Ht", v: "13\u201906\u201d" },
        { icon: Weight, k: "GVW", v: "80k" },
        { icon: ShieldCheck, k: "Hazmat", v: "Clear" },
      ].map((r) => (
        <MiniCard key={r.k} className="!p-1.5 text-center">
          <div className="text-[8px] text-white/60 uppercase tracking-widest">{r.k}</div>
          <div
            className="mt-0.5 text-[11px] font-extrabold text-white"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
          >
            {r.v}
          </div>
        </MiniCard>
      ))}
    </div>
  </PhoneFrame>
);

const MockDieselIntel = () => (
  <PhoneFrame testId="mock-diesel-intel">
    <TopPill label="Diesel Intel" tone="amber" />
    <ScreenTitle>Cheapest along the route.</ScreenTitle>
    <div className="mt-3 space-y-1.5">
      {[
        { k: "Love's · Amarillo, TX", d: "Exit 74", v: "$3.62", tag: "best" },
        { k: "Pilot · Little Rock, AR", d: "I-40 mile 155", v: "$3.79" },
        { k: "TA · Nashville, TN", d: "I-40 mile 210", v: "$3.96" },
      ].map((r) => (
        <MiniCard
          key={r.k}
          className="!p-2 flex items-center gap-2"
          style={
            r.tag === "best"
              ? {
                  background:
                    "linear-gradient(120deg, rgba(244,199,82,0.14) 0%, rgba(244,199,82,0.03) 100%)",
                  border: "1px solid rgba(244,199,82,0.45)",
                }
              : undefined
          }
        >
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
            style={{
              background: "rgba(244,199,82,0.14)",
              border: "1px solid rgba(244,199,82,0.40)",
            }}
          >
            <Fuel size={12} className="text-[#F4C752]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-white font-semibold truncate">{r.k}</div>
            <div className="text-[8px] text-white/60 truncate">{r.d}</div>
          </div>
          <div
            className="text-[11px] font-extrabold text-white shrink-0"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
          >
            {r.v}
          </div>
          {r.tag === "best" && (
            <TrendingDown size={11} className="text-[#F4C752] shrink-0" strokeWidth={3} />
          )}
        </MiniCard>
      ))}
    </div>
    <MiniCard
      className="mt-3"
      style={{
        background:
          "linear-gradient(160deg, rgba(244,199,82,0.16) 0%, rgba(244,199,82,0.04) 100%)",
        border: "1px solid rgba(244,199,82,0.42)",
      }}
    >
      <div className="text-[8px] uppercase tracking-[0.18em] text-[#F4C752] font-bold">
        EIA reference feed
      </div>
      <div className="mt-1 flex items-center justify-between">
        <div
          className="text-[13px] font-extrabold text-white"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          Save $54 this leg
        </div>
        <div className="text-[9px] text-[#F4C752] font-bold">− $0.34/gal</div>
      </div>
    </MiniCard>
  </PhoneFrame>
);

const MockIFTA = () => (
  <PhoneFrame testId="mock-ifta">
    <TopPill label="IFTA · Q3" tone="amber" />
    <ScreenTitle>Quarterly log, ready to file.</ScreenTitle>
    <MiniCard className="mt-3">
      <div className="text-[8px] uppercase tracking-[0.18em] text-white/60 font-bold">
        Miles by jurisdiction
      </div>
      <div className="mt-2 space-y-1.5">
        {[
          { k: "CO", m: "3,412", g: "481" },
          { k: "KS", m: "2,180", g: "307" },
          { k: "MO", m: "1,844", g: "260" },
          { k: "AR", m: "1,290", g: "182" },
          { k: "TN", m: "2,015", g: "284" },
        ].map((r) => (
          <div key={r.k} className="flex items-center text-[10px]">
            <span className="w-8 text-[#F4C752] font-bold">{r.k}</span>
            <span className="flex-1 text-white/80">{r.m} mi</span>
            <span className="text-white font-semibold">{r.g} gal</span>
          </div>
        ))}
      </div>
    </MiniCard>
    <MiniCard
      className="mt-3"
      style={{
        background:
          "linear-gradient(160deg, rgba(244,199,82,0.16) 0%, rgba(244,199,82,0.04) 100%)",
        border: "1px solid rgba(244,199,82,0.42)",
      }}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[8px] uppercase tracking-[0.18em] text-[#F4C752] font-bold">
            IFTA report
          </div>
          <div
            className="text-[13px] font-extrabold text-white"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
          >
            Q3 · 10,741 mi
          </div>
        </div>
        <div
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-bold"
          style={{
            background: "#F4C752",
            color: "#04111F",
          }}
        >
          <FileText size={10} />
          Export
        </div>
      </div>
    </MiniCard>
  </PhoneFrame>
);

// =============================================================================
// FREELANCER MOCKS — Invoice landing, tax auto-stash, CRM ledger
// =============================================================================
const MockInvoiceLanded = () => (
  <PhoneFrame testId="mock-invoice-landed">
    <TopPill label="Invoice landed" tone="purple" />
    <ScreenTitle>Payment received.</ScreenTitle>
    <MiniCard
      className="mt-3"
      style={{
        background:
          "linear-gradient(160deg, rgba(183,165,255,0.20) 0%, rgba(183,165,255,0.06) 100%)",
        border: "1px solid rgba(183,165,255,0.45)",
      }}
    >
      <div className="flex items-center justify-between">
        <div className="text-[8px] uppercase tracking-[0.20em] text-[#B7A5FF] font-bold">
          Stripe deposit
        </div>
        <ArrowUpRight size={12} className="text-[#B7A5FF]" />
      </div>
      <div
        className="mt-0.5 text-[22px] font-extrabold text-white leading-none"
        style={{ fontFamily: "Sora, Inter, sans-serif" }}
      >
        $4,850<span className="text-white/60 text-[13px]">.00</span>
      </div>
      <div className="mt-1 text-[9px] text-white/70">Invoice #INV-2141 · Halcyon Studio</div>
    </MiniCard>
    <div className="mt-2.5 space-y-1.5">
      {[
        { k: "Category", v: "Design services" },
        { k: "Client", v: "Halcyon Studio" },
        { k: "Schedule-C bucket", v: "Gross revenue" },
      ].map((r) => (
        <div key={r.k} className="flex items-center justify-between text-[10px]">
          <span className="text-white/70">{r.k}</span>
          <span className="text-white font-semibold">{r.v}</span>
        </div>
      ))}
    </div>
    <div
      className="mt-3 flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[9px] font-bold"
      style={{
        background: "rgba(34,197,133,0.10)",
        border: "1px solid rgba(34,197,133,0.35)",
        color: "#22C585",
      }}
    >
      <Check size={11} strokeWidth={3} />
      Auto-categorized to gross revenue
    </div>
  </PhoneFrame>
);

const MockTaxAutoStash = () => (
  <PhoneFrame testId="mock-tax-autostash">
    <TopPill label="Tax auto-stash" tone="purple" />
    <ScreenTitle>30% moved to shield.</ScreenTitle>
    <MiniCard
      className="mt-3"
      style={{
        background:
          "linear-gradient(160deg, rgba(183,165,255,0.20) 0%, rgba(183,165,255,0.06) 100%)",
        border: "1px solid rgba(183,165,255,0.45)",
      }}
    >
      <div className="text-[8px] uppercase tracking-[0.20em] text-[#B7A5FF] font-bold">
        Quarterly tax reserve
      </div>
      <div className="mt-0.5 flex items-baseline gap-1">
        <span
          className="text-[22px] font-extrabold text-white leading-none"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          $1,455
        </span>
        <span className="text-[11px] text-[#B7A5FF] font-bold">+ from INV-2141</span>
      </div>
      <div className="mt-1 text-[9px] text-white/70">
        Segregated envelope · not counted as Safe to Spend
      </div>
    </MiniCard>
    <MiniCard className="mt-3">
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-white/70">Q4 tax reserve</span>
        <span className="text-[#B7A5FF] font-bold">62% of target</span>
      </div>
      <div
        className="mt-1.5 w-full h-1.5 rounded-full overflow-hidden"
        style={{ background: "rgba(255,255,255,0.10)" }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: "62%",
            background: "linear-gradient(90deg, #B7A5FF 0%, #DED2FF 100%)",
          }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-[9px] text-white/60">
        <span>$6,480 / $10,500</span>
        <span>Due Jan 15</span>
      </div>
    </MiniCard>
    <div
      className="mt-3 flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[9px] font-bold"
      style={{
        background: "rgba(183,165,255,0.10)",
        border: "1px solid rgba(183,165,255,0.35)",
        color: "#B7A5FF",
      }}
    >
      <ShieldCheck size={11} strokeWidth={2.5} />
      Freelance Shield engaged
    </div>
  </PhoneFrame>
);

const MockCRMLedger = () => (
  <PhoneFrame testId="mock-crm-ledger">
    <TopPill label="Client Ledger" tone="purple" />
    <ScreenTitle>CRM-ready · 1099-clean.</ScreenTitle>
    <div className="mt-3 space-y-1.5">
      {[
        { icon: Building2, k: "Halcyon Studio", d: "3 invoices", v: "$14,550", badge: "1099" },
        { icon: Briefcase, k: "Nordwind Co.", d: "5 invoices", v: "$9,820", badge: "1099" },
        { icon: Wallet, k: "Meridian Labs", d: "2 invoices", v: "$6,200", badge: "W-9" },
      ].map((r) => (
        <MiniCard key={r.k} className="!p-2 flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
            style={{
              background: "rgba(183,165,255,0.14)",
              border: "1px solid rgba(183,165,255,0.40)",
            }}
          >
            <r.icon size={13} className="text-[#B7A5FF]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] text-white font-semibold truncate">{r.k}</div>
            <div className="text-[8px] text-white/60 truncate">{r.d}</div>
          </div>
          <div className="text-[10px] text-white font-bold shrink-0">{r.v}</div>
          <div
            className="px-1.5 py-0.5 rounded-md text-[7px] font-extrabold uppercase tracking-wider shrink-0"
            style={{
              background: "rgba(183,165,255,0.16)",
              border: "1px solid rgba(183,165,255,0.45)",
              color: "#B7A5FF",
            }}
          >
            {r.badge}
          </div>
        </MiniCard>
      ))}
    </div>
    <MiniCard
      className="mt-3 flex items-center justify-between"
      style={{
        background:
          "linear-gradient(160deg, rgba(183,165,255,0.16) 0%, rgba(183,165,255,0.04) 100%)",
        border: "1px solid rgba(183,165,255,0.42)",
      }}
    >
      <div>
        <div className="text-[8px] uppercase tracking-[0.18em] text-[#B7A5FF] font-bold">
          Export to
        </div>
        <div
          className="text-[12px] font-extrabold text-white"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
        >
          QuickBooks · Wave
        </div>
      </div>
      <div
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-bold"
        style={{ background: "#B7A5FF", color: "#04111F" }}
      >
        <FileText size={10} />
        CSV
      </div>
    </MiniCard>
  </PhoneFrame>
);

// =============================================================================
// TIMELINE STEP (matches Mission Stories rail pattern)
// =============================================================================
const TimelineStep = ({ index, isLast, label, title, description, mock, testId, accent }) => (
  <div
    className="relative grid grid-cols-[44px_1fr] md:grid-cols-[52px_1fr] gap-4 md:gap-5"
    data-testid={testId}
  >
    <div className="relative flex flex-col items-center">
      <div
        className="relative z-10 rounded-full flex items-center justify-center font-extrabold text-[#04111F]"
        style={{
          background: accent,
          boxShadow: `0 12px 30px -12px ${accent}bb, inset 0 1px 0 rgba(255,255,255,0.35)`,
          fontFamily: "Sora, Inter, sans-serif",
          fontSize: 18,
          width: 44,
          height: 44,
        }}
      >
        {index}
      </div>
      {!isLast && (
        <div
          className="flex-1 w-[2px] mt-1"
          style={{
            background: accent,
            opacity: 0.7,
            boxShadow: `0 0 12px ${accent}55`,
          }}
        />
      )}
    </div>
    <div className={`${isLast ? "pb-0" : "pb-6 md:pb-8"}`}>
      <div
        className="text-[10px] uppercase tracking-[0.22em] font-bold"
        style={{ color: accent }}
      >
        {label}
      </div>
      <h4
        className="mt-1.5 text-lg md:text-xl font-extrabold text-white leading-tight"
        style={{ fontFamily: "Sora, Inter, sans-serif" }}
      >
        {title}
      </h4>
      <p className="mt-1.5 text-[13.5px] md:text-[14px] text-[#C7D0DF] leading-relaxed">
        {description}
      </p>
      <div className="mt-3">{mock}</div>
    </div>
  </div>
);

const PersonaHeader = ({ icon: Icon, tone, tag, name, subtitle, headline, scenario, testId }) => {
  const palette = {
    amber: {
      accent: "#F4C752",
      bg: "linear-gradient(140deg, rgba(244,199,82,0.22) 0%, rgba(244,199,82,0.06) 100%)",
      border: "rgba(244,199,82,0.55)",
    },
    purple: {
      accent: "#B7A5FF",
      bg: "linear-gradient(140deg, rgba(183,165,255,0.22) 0%, rgba(183,165,255,0.06) 100%)",
      border: "rgba(183,165,255,0.55)",
    },
  }[tone];
  return (
    <div className="mb-6" data-testid={testId}>
      <div className="flex items-center gap-4">
        <div className="relative shrink-0">
          <div
            className="w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center overflow-hidden"
            style={{
              background: palette.bg,
              border: `1px solid ${palette.border}`,
              boxShadow: `0 20px 40px -20px ${palette.accent}55, inset 0 1px 0 rgba(255,255,255,0.10)`,
            }}
          >
            <Icon size={28} style={{ color: palette.accent }} />
          </div>
          <div
            className="absolute -bottom-1 -right-1 px-1.5 py-0.5 rounded-md text-[9px] font-extrabold tracking-widest"
            style={{
              background: "#04111F",
              border: `1px solid ${palette.border}`,
              color: palette.accent,
              fontFamily: "Sora, Inter, sans-serif",
            }}
          >
            {tag}
          </div>
        </div>
        <div className="min-w-0">
          <div
            className="text-lg md:text-xl font-extrabold text-white leading-tight"
            style={{ fontFamily: "Sora, Inter, sans-serif" }}
          >
            {name}
          </div>
          <div className="mt-0.5 text-[11px] text-[#A8B0BE] uppercase tracking-[0.18em]">
            {subtitle}
          </div>
        </div>
      </div>
      <div
        className="mt-4 text-[11px] uppercase tracking-[0.22em] font-bold"
        style={{ color: palette.accent }}
      >
        {headline}
      </div>
      <p className="mt-1.5 text-[14px] text-[#C7D0DF] leading-relaxed">{scenario}</p>
    </div>
  );
};

const StoryPanel = ({ testId, accent, header, steps, footerCallout }) => (
  <div
    className="rounded-3xl p-6 md:p-7 relative overflow-hidden h-full flex flex-col"
    data-testid={testId}
    style={{
      background:
        "linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow:
        "0 40px 80px -40px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
    }}
  >
    <div
      aria-hidden="true"
      className="absolute -top-24 -right-24 w-72 h-72 rounded-full opacity-20 blur-3xl"
      style={{ background: accent }}
    />
    <div className="relative flex-1">
      {header}
      <div className="mt-2">
        {steps.map((step, i) => (
          <TimelineStep
            key={step.title}
            index={i + 1}
            isLast={i === steps.length - 1}
            accent={accent}
            {...step}
            testId={`${testId}-step-${i + 1}`}
          />
        ))}
      </div>
      {footerCallout}
    </div>
  </div>
);

const RealityStories = () => (
  <section
    id="reality-stories"
    className="relative py-14 md:py-20"
    data-testid="section-reality-stories"
  >
    <div className="mx-auto max-w-[1200px] px-4 md:px-6">
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#22C585]">
          Reality Stories
        </div>
        <h2
          className="mt-3 text-3xl md:text-4xl font-extrabold text-white leading-tight tracking-tight"
          style={{ fontFamily: "Sora, Inter, sans-serif" }}
          data-testid="reality-stories-title"
        >
          The road and the ledger — covered.
        </h2>
        <p className="mt-3 text-[15px] text-[#C7D0DF] leading-relaxed">
          Same Nerve Center. Different missions. Two more real-world flows for
          the people SpendNova was built to defend.
        </p>
      </div>

      <div className="mt-12 grid gap-6 md:gap-8 lg:grid-cols-2 items-stretch">
        {/* STORY — THE LONG HAUL (Trucker) */}
        <StoryPanel
          testId="reality-trucker"
          accent="#F4C752"
          header={
            <PersonaHeader
              icon={Truck}
              tone="amber"
              tag="OTR"
              name="Marcus"
              subtitle="Owner-operator · Cross-country freight"
              headline="The Long Haul"
              scenario="Running 2,000 miles Denver to Jacksonville with a Class 8 rig, refrigerated trailer, and a Sunday deadline. Every mile costs money — or saves it."
              testId="reality-trucker-header"
            />
          }
          steps={[
            {
              label: "Step 01 · HGV route",
              title: "Truck-safe routing (ORS)",
              description:
                "Marcus drops his origin, destination, and rig profile. The Nerve Center plots an HGV-aware route through OpenRouteService — respecting 13'6\" bridge clearance, 80,000-lb weight limits, and hazmat corridors.",
              mock: <MockHGVRoute />,
            },
            {
              label: "Step 02 · Diesel intel",
              title: "EIA-tracked prices, cheapest per gallon.",
              description:
                "Live diesel intel from the EIA feed pinpoints the cheapest fill-up along the corridor. Marcus stops at Love's in Amarillo — saves $0.34/gal, roughly $54 on this leg alone.",
              mock: <MockDieselIntel />,
            },
            {
              label: "Step 03 · IFTA log",
              title: "Quarterly fuel-tax report, exported clean.",
              description:
                "Miles by jurisdiction, gallons by state, everything logged automatically. When Q3 closes, Marcus exports the IFTA report as a CSV — no scrambling, no missed jurisdictions.",
              mock: <MockIFTA />,
            },
          ]}
          footerCallout={
            <div
              className="mt-6 rounded-2xl p-4 flex items-start gap-3"
              style={{
                background:
                  "linear-gradient(120deg, rgba(244,199,82,0.14) 0%, rgba(244,199,82,0.04) 100%)",
                border: "1px solid rgba(244,199,82,0.35)",
              }}
              data-testid="reality-trucker-callout"
            >
              <Route size={18} className="text-[#F4C752] shrink-0 mt-0.5" />
              <div className="text-[13px] text-white leading-snug">
                <span className="font-bold">Truck-safe by default.</span>{" "}
                <span className="text-[#C7D0DF]">
                  Height, weight, length, and hazmat constraints locked before
                  the tires roll.
                </span>
              </div>
            </div>
          }
        />

        {/* STORY — THE FREELANCE SHIELD */}
        <StoryPanel
          testId="reality-freelancer"
          accent="#B7A5FF"
          header={
            <PersonaHeader
              icon={Briefcase}
              tone="purple"
              tag="1099"
              name="Nova"
              subtitle="Creative director · Solo studio"
              headline="The Freelance Shield"
              scenario="Running a design studio out of a home office. Three retainer clients, quarterly estimated taxes, and zero interest in a shoebox full of receipts."
              testId="reality-freelancer-header"
            />
          }
          steps={[
            {
              label: "Step 01 · Invoice landed",
              title: "Payment lands, categorized in real time.",
              description:
                "Nova's Stripe invoice clears. SpendNova auto-categorizes the deposit to gross revenue and files it under Halcyon Studio's client ledger — zero manual bookkeeping.",
              mock: <MockInvoiceLanded />,
            },
            {
              label: "Step 02 · Tax auto-stash",
              title: "30% shielded before Nova even sees it.",
              description:
                "The Freelance Shield rule instantly moves 30% ($1,455) into a segregated quarterly tax envelope — kept out of Safe to Spend so April 15 never sneaks up.",
              mock: <MockTaxAutoStash />,
            },
            {
              label: "Step 03 · CRM ledger",
              title: "CRM-ready, 1099-clean export.",
              description:
                "When it's time to close the books or hand the CPA a file, Nova exports a client-by-client ledger — 1099 status flagged — straight to QuickBooks, Wave, or HoneyBook.",
              mock: <MockCRMLedger />,
            },
          ]}
          footerCallout={
            <div
              className="mt-6 rounded-2xl p-4 flex items-start gap-3"
              style={{
                background:
                  "linear-gradient(120deg, rgba(183,165,255,0.14) 0%, rgba(183,165,255,0.04) 100%)",
                border: "1px solid rgba(183,165,255,0.35)",
              }}
              data-testid="reality-freelancer-callout"
            >
              <ShieldCheck size={18} className="text-[#B7A5FF] shrink-0 mt-0.5" />
              <div className="text-[13px] text-white leading-snug">
                <span className="font-bold">Freelance Shield engaged.</span>{" "}
                <span className="text-[#C7D0DF]">
                  Quarterly reserves segregated · tax season stops being an
                  ambush.
                </span>
              </div>
            </div>
          }
        />
      </div>
    </div>
  </section>
);

export default RealityStories;
